import { ref } from 'vue';
import { UpdateChartsRecidAndStyleUrlSearchParams, updateCharts } from '@/composables/updateCharts';
import { refreshChartLists } from '@/composables/refreshChartLists';
import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
import { getPeriodOptionFromPeriodValue } from '@/enums/period-options';
import { getChartListFromCache } from '@/composables/getChartListFromCache';
import { INVALID_CHART_LIST_LIST_NUM, isValidChartListListNum } from '@/composables/isValidChartListListNum';
import { isValidChartRecid } from '@/composables/isValidChartRecid';
import { ChartList } from '@/interfaces/chartList';
import { SimpleFrontendChart } from '@/interfaces/chart';

const chartRecidsToApplyChartStyleTo = ref<number[]>([]);
const chartListListNumToApplyChartStyleTo = ref<number>(INVALID_CHART_LIST_LIST_NUM);

function clearChartListAndChartsToApplyChartStyleTo() {
  chartListListNumToApplyChartStyleTo.value = INVALID_CHART_LIST_LIST_NUM;
  chartRecidsToApplyChartStyleTo.value = [];
}

function setChartListAndChartsToApplyChartStyleTo(chartListToApplyChartStyleTo: ChartList, charts: SimpleFrontendChart[]) {
  chartListListNumToApplyChartStyleTo.value = chartListToApplyChartStyleTo.listNum;
  chartRecidsToApplyChartStyleTo.value = charts.map(chart => chart.recid);
}

async function applyChartStyleToCharts(styleUrlSearchParams: URLSearchParams, doNotChangePeriodSettings: boolean) {
  const chartListListNum = chartListListNumToApplyChartStyleTo.value;
  const chartRecids = Array.from(new Set(chartRecidsToApplyChartStyleTo.value));
  const numChartsBeingAffected = chartRecids.length;

  if (
    !isValidChartListListNum(chartListListNum) ||
    !numChartsBeingAffected
  ) {
    return {
      numFailures: numChartsBeingAffected,
      numSuccesses: 0
    };
  }

  await refreshChartLists();
  await loadChartsForChartList(undefined, chartListListNum, true);

  const chartList = getChartListFromCache(chartListListNum);

  if (!chartList || chartList.isReadonly) {
    return {
      numFailures: numChartsBeingAffected,
      numSuccesses: 0
    };
  }

  const updateChartsRecidsAndStyleUrlSearchParams: UpdateChartsRecidAndStyleUrlSearchParams[] = [];

  for (const chartRecid of chartRecids) {
    if (!isValidChartRecid(chartRecid)) {
      continue;
    }

    const chart = chartList.charts.find(chartInChartList => chartInChartList.recid === chartRecid);

    if (!chart) {
      continue;
    }

    const styleUrlParametersForCurrentChart = new URLSearchParams(styleUrlSearchParams);

    if (doNotChangePeriodSettings && chart.styleCacheSettingsOverrides.period) {
      const periodOption = getPeriodOptionFromPeriodValue(chart.styleCacheSettingsOverrides.period);

      styleUrlParametersForCurrentChart.set('p', periodOption.sharpCharts2Id);
    }

    updateChartsRecidsAndStyleUrlSearchParams.push({
      chartRecid: chart.recid,
      styleUrlSearchParams: styleUrlParametersForCurrentChart
    });
  }

  if (!updateChartsRecidsAndStyleUrlSearchParams.length) {
    return {
      numFailures: numChartsBeingAffected,
      numSuccesses: 0
    };
  }

  clearChartListAndChartsToApplyChartStyleTo();

  let errorCount = 0;

  await updateCharts(updateChartsRecidsAndStyleUrlSearchParams)
    .then(jsonLayerResponses => {
      window.sc3WorkbenchLogger.debug(`Successfully updated ChartStyle for ${numChartsBeingAffected} charts from applyChartStyleToCharts.`, jsonLayerResponses);

      for (const jsonLayerResponse of jsonLayerResponses) {
        if (!jsonLayerResponse.success) {
          errorCount++;
        }
      }
    }).catch(e => {
      window.sc3WorkbenchLogger.error(`Failed to update ChartStyle for ${numChartsBeingAffected} charts from applyChartStyleToCharts - error occurred while updating.`, e);

      errorCount = numChartsBeingAffected;
    });

  await refreshChartLists();
  await loadChartsForChartList(undefined, chartListListNum, true);

  return { // TODO: we shouldn't be concerned with no errors, some errors, or all errors. That should be a backend thing to tell us "error" or "no error". Will be swapped once we get a proper bulk-api for this
    numFailures: errorCount,
    numSuccesses: numChartsBeingAffected - errorCount
  };
}

export function useApplyChartStyle() {
  return {
    applyChartStyleToCharts,
    setChartListAndChartsToApplyChartStyleTo
  }
}
